import React, { useCallback, useEffect, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { ActionButton } from "../../components/ui/ActionButton";
import { SuccessToast } from "../../components/ui/ResearchQueue";
import { translationsApi } from "../../utils/api";
import { apiErrorMessage, languageName } from "../../utils/helpers";
import { STATUS_LABELS, TranslationRow } from "./TranslationRow";
import { translationRowActions } from "./rowActions";

// Where a document shows a text (`uses`, #840), in words. A raw description can be both the shown
// description and the card summary's fallback, and then it is one text with both labels.
const FIELD_LABELS = {
  venue_type_hint: "Venue: type",
  public_transport_access: "Venue: getting there",
  venue_inferred_context: "Venue: about",
};

function labelOf({ family, field }) {
  if (family === "description") return "Description";
  if (family === "card_summary") return field === "inferred_context" ? "Card summary" : "Card summary (the description)";
  return FIELD_LABELS[field] ?? field;
}

// What Translate now would send: a text with no row in a locale, or a failed one (`select_due`).
const isDue = ({ row }) => !row || row.status === "failed";

// One language's line: how many of the document's texts are in each state, in the row badges' words.
const SUMMARY_LABELS = { ...STATUS_LABELS, missing: "Missing" };

function languageSummary(texts, locale) {
  const counts = {};
  for (const text of texts) {
    const { row } = text.translations.find((t) => t.locale === locale) ?? {};
    const state = row ? row.status : "missing";
    counts[state] = (counts[state] ?? 0) + 1;
  }
  // The badges' order, then any state they do not name, so none is ever left out of the line.
  const states = [...new Set([...Object.keys(SUMMARY_LABELS), ...Object.keys(counts)])].filter((state) => counts[state]);
  const parts = states.map((state) => `${counts[state]} ${(SUMMARY_LABELS[state] ?? state).toLowerCase()}`);
  return `${languageName(locale)}: ${parts.join(" · ") || "nothing to translate"}`;
}

/**
 * The translations of one event or venue (#840), in its detail modal: every text the public payload
 * localizes for it — an event's shown description, card summary and venue prose; a venue's prose —
 * per target language, each with the Translations page's own row card and actions, and Translate now
 * for whatever has no row yet (or failed). Collapsed by default, with one summary line per language.
 * Every action re-reads the panel in place.
 */
export function DocumentTranslations({ type, id }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [busy, setBusy] = useState(null);

  // Callers key the panel on the document, so another document is a fresh mount: a read still in
  // flight for the old one lands on an unmounted panel and changes nothing.
  const load = useCallback(async () => {
    try {
      setData(await translationsApi.getForDocument(type, id));
      setLoadError("");
    } catch (err) {
      setLoadError(apiErrorMessage(err, "Could not load the translations"));
    }
  }, [type, id]);

  useEffect(() => {
    load();
  }, [load]);

  // The toast clears itself, as on the queue pages, so an old success never sits beside a new error.
  useEffect(() => {
    if (!success) return undefined;
    const timer = setTimeout(() => setSuccess(""), 4000);
    return () => clearTimeout(timer);
  }, [success]);

  // One action at a time, like the queue pages: `ActionButton` disables every button while one runs.
  const perform = async (key, action, message, { reload = true } = {}) => {
    if (busy) return;
    setBusy(key);
    setError("");
    try {
      const outcome = await action();
      setSuccess(typeof outcome === "string" ? outcome : (message ?? ""));
      if (reload) await load();
    } catch (err) {
      setError(apiErrorMessage(err, "The action failed"));
    } finally {
      setBusy(null);
    }
  };

  const { save, approve, retranslate, discard } = translationRowActions({
    run: perform,
    suggest: (key, action) => perform(key, action, undefined, { reload: false }),
    // An event's own texts are retranslated with its context; a venue's prose has no event.
    // The API names it (`context_event_id`): a legacy event's `_source.id` is not its document id.
    contextEventId: () => data?.context_event_id ?? undefined,
  });

  const texts = data?.texts ?? [];
  const locales = data?.target_locales ?? [];
  const due = texts.flatMap((text) => text.translations).filter(isDue).length;

  // A run that failed part-way may still have written rows, so the panel is re-read either way.
  const translateNow = () =>
    perform("translate-now", async () => {
      const result = await translationsApi.translateDocument(type, id);
      if (result.success) return result.message;
      setError(result.message);
      return null;
    });

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <button
          type="button"
          className="flex items-center gap-1 text-sm font-semibold uppercase tracking-wide text-muted-foreground hover:text-foreground"
          aria-expanded={open}
          onClick={() => setOpen((value) => !value)}
        >
          {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          Translations
        </button>
        {data && texts.length > 0 && (
          <ActionButton
            busy={busy}
            actionKey="translate-now"
            disabled={!due}
            title={due ? "Translates every text with no translation yet, or a failed one" : "Nothing is missing"}
            onClick={translateNow}
          >
            Translate now ({due} missing)
          </ActionButton>
        )}
      </div>

      {loadError && <p className="text-sm text-red-700">{loadError}</p>}
      {data && !texts.length && <p className="text-xs text-muted-foreground">No text to translate.</p>}
      {data &&
        texts.length > 0 &&
        locales.map((locale) => (
          <p key={locale} className="text-xs text-muted-foreground">
            {languageSummary(texts, locale)}
          </p>
        ))}
      {error && <div className="rounded-md border border-red-500/20 bg-red-500/10 p-2 text-sm text-red-700">{error}</div>}
      <SuccessToast message={success} />

      {open && data && (
        <div className="space-y-4">
          {texts.map((text) => (
            <div key={`${text.kind}:${text.key}`} className="space-y-2">
              <p className="text-xs font-medium">{text.uses.map(labelOf).join(" · ")}</p>
              {text.translations.map(({ locale, row }) =>
                row ? (
                  <TranslationRow
                    key={`${locale}:${row.status}:${row.updated_at}`}
                    row={row}
                    busy={busy}
                    showStatus
                    onSave={save}
                    onApprove={approve}
                    onRetranslate={retranslate}
                    onDiscard={discard}
                  />
                ) : (
                  <div key={locale} className="rounded-md border border-dashed px-3 py-2 text-xs text-muted-foreground">
                    <p className="font-medium">Not translated yet into {languageName(locale)}</p>
                    <p className="mt-1 line-clamp-2 whitespace-pre-wrap">{text.source_text}</p>
                  </div>
                )
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
